import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useDebounce } from '@/hooks/useDebounce';
import { legacyRoutes } from '@/lib/legacy';
import {
  fetchInstallationQualifications,
} from '../api';
import {
  useInstallationQualificationAccounts,
  useInstallationQualifications,
  useInstallationQualification,
} from '../hooks';
import {
  copyInstallationQualificationsToClipboard,
  exportInstallationQualificationsToCSV,
} from '../utils/csvExport';
import { InstallationQualificationsTable } from '../components/InstallationQualificationsTable';
import type {
  InstallationQualification,
  InstallationQualificationsFilters,
} from '../types';

const COLUMN_FILTERS_STORAGE_KEY = 'atek-installation-qualifications-column-filters';

function loadColumnFilters(searchParams?: URLSearchParams): Record<string, string> {
  const reportNumber = searchParams?.get('reportNumber');
  if (reportNumber) {
    return { reportNumber };
  }

  if (searchParams?.get('iqId') || searchParams?.get('id')) {
    return {};
  }

  try {
    const stored = localStorage.getItem(COLUMN_FILTERS_STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {
    // Ignore storage errors.
  }
  return {};
}

function parseDateRange(value: string | undefined): { from?: string; to?: string } {
  if (!value) return {};
  const [from, to] = value.split('>>');
  return {
    from: from || undefined,
    to: to || undefined,
  };
}

export default function InstallationQualificationsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const focusedIqId = searchParams.get('iqId') || searchParams.get('id') || undefined;
  const focusedReportNumber = searchParams.get('reportNumber') || undefined;
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(() => loadColumnFilters(searchParams));
  const [page, setPage] = useState(0);
  const [pageSize] = useState(25);
  const [sortKey, setSortKey] = useState<InstallationQualificationsFilters['sortKey']>('reportedAt');
  const [sortDirection, setSortDirection] = useState<1 | -1>(-1);
  const [isExportingAll, setIsExportingAll] = useState(false);
  const focusedIqQuery = useInstallationQualification(focusedIqId);
  const urlReportNumber = focusedReportNumber || focusedIqQuery.data?.reportNumber;

  useEffect(() => {
    if (!urlReportNumber) return;
    setColumnFilters({ reportNumber: urlReportNumber });
    setPage(0);
  }, [urlReportNumber]);

  useEffect(() => {
    localStorage.setItem(COLUMN_FILTERS_STORAGE_KEY, JSON.stringify(columnFilters));
  }, [columnFilters]);

  const debouncedColumnFilters = useDebounce(columnFilters, 300);

  useEffect(() => {
    setPage(0);
  }, [debouncedColumnFilters]);

  const filters = useMemo<InstallationQualificationsFilters>(() => {
    const createdRange = parseDateRange(debouncedColumnFilters.reportedAt);
    const verifiedRange = parseDateRange(debouncedColumnFilters.verifiedAt);

    return {
      reportNumber: debouncedColumnFilters.reportNumber || undefined,
      accountId: debouncedColumnFilters.organisation || undefined,
      sensor: debouncedColumnFilters.sensor || undefined,
      transmitter: debouncedColumnFilters.transmitter || undefined,
      responsible: debouncedColumnFilters.responsible || undefined,
      createdFrom: createdRange.from,
      createdTo: createdRange.to,
      verifiedBy: debouncedColumnFilters.verifiedBy || undefined,
      verifiedFrom: verifiedRange.from,
      verifiedTo: verifiedRange.to,
      state: debouncedColumnFilters.state || undefined,
      qualificationType: debouncedColumnFilters.qualificationType as InstallationQualificationsFilters['qualificationType'] || undefined,
      sortKey,
      sortDirection,
      pageNumber: page,
      pageLength: pageSize,
    };
  }, [debouncedColumnFilters, page, pageSize, sortDirection, sortKey]);

  const listQuery = useInstallationQualifications(filters);
  const accountsQuery = useInstallationQualificationAccounts();
  const rows = listQuery.data?.installationQualifications ?? [];
  const total = listQuery.data?.total ?? 0;

  const clearFocusedIqParams = () => {
    if (!focusedIqId && !focusedReportNumber) return;
    const next = new URLSearchParams(searchParams);
    next.delete('iqId');
    next.delete('id');
    next.delete('reportNumber');
    setSearchParams(next, { replace: true });
  };

  const setColumnFilter = (key: string, value: string) => {
    setColumnFilters((current) => ({ ...current, [key]: value }));
    if (key === 'reportNumber' && value !== urlReportNumber) {
      clearFocusedIqParams();
    }
  };

  const clearColumnFilters = () => {
    setColumnFilters({});
    setPage(0);
    clearFocusedIqParams();
  };

  const handleSort = (key: InstallationQualificationsFilters['sortKey']) => {
    if (key === sortKey) {
      setSortDirection((current) => (current === 1 ? -1 : 1));
    } else {
      setSortKey(key);
      setSortDirection(1);
    }
    setPage(0);
  };

  const handleCopy = async () => {
    await copyInstallationQualificationsToClipboard(rows);
    toast.success('Copied current page to clipboard');
  };

  const handleExportPage = () => {
    exportInstallationQualificationsToCSV(rows, 'installation-qualifications-page');
    toast.success(`Exported ${rows.length} rows`);
  };

  const handleExportAll = async () => {
    try {
      setIsExportingAll(true);
      const allRows: InstallationQualification[] = [];
      const batchSize = 100;
      let currentPage = 0;
      let totalCount = 0;

      do {
        const result = await fetchInstallationQualifications({
          ...filters,
          pageNumber: currentPage,
          pageLength: batchSize,
        });
        allRows.push(...(result.installationQualifications || []));
        totalCount = result.total ?? 0;
        currentPage++;
      } while (allRows.length < totalCount);

      exportInstallationQualificationsToCSV(allRows, 'installation-qualifications-all');
      toast.success(`Exported ${allRows.length} rows`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to export all installation qualifications');
    } finally {
      setIsExportingAll(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Installation Qualification</h1>
          <p className="text-muted-foreground">
            PostgreSQL-backed installation qualification archive with server-side filters and export.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link to="/operations/installations">
            <Button>
              <Wrench className="mr-2 h-4 w-4" />
              Create IQ for Sensor
            </Button>
          </Link>
          <Button
            variant="outline"
            onClick={() => window.open(legacyRoutes.installationQualification(), '_blank')}
          >
            Gateway IQ (Legacy)
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Installation Qualifications</CardTitle>
          <CardDescription>
            Mirrors the legacy validation table while reading from the PostgreSQL IQ projection.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <InstallationQualificationsTable
            rows={rows}
            total={total}
            page={page}
            pageSize={pageSize}
            sortKey={sortKey}
            sortDirection={sortDirection}
            columnFilters={columnFilters}
            accountOptions={accountsQuery.data ?? []}
            isLoading={listQuery.isLoading}
            isRefreshing={listQuery.isFetching}
            isExportingAll={isExportingAll}
            onSort={handleSort}
            onPageChange={setPage}
            onColumnFilterChange={setColumnFilter}
            onClearColumnFilters={clearColumnFilters}
            onCopy={handleCopy}
            onExportPage={handleExportPage}
            onExportAll={handleExportAll}
            onRefresh={() => void listQuery.refetch()}
          />
        </CardContent>
      </Card>
    </div>
  );
}
